// Blocks & cleans any non-ASCII input (no Cyrillic/accents) in v-text-field/textarea.

const NON_ASCII_RE = /[\u0080-\u{10FFFF}]/u;
const NON_ASCII_RE_GLOBAL = /[\u0080-\u{10FFFF}]/gu;

const hasNonAscii = s => NON_ASCII_RE.test(s || '');
const sanitizeAscii = s => (s || '').replace(NON_ASCII_RE_GLOBAL, '');

function setCursorPosition(input, position) {
  if (!input.setSelectionRange) return;

  try {
    input.setSelectionRange(position, position);
  } catch (error) {
    console.warn('Failed to set cursor position:', error);
  }
}

function findVuetifyWrappers(element) {
  const root = element.closest?.('.v-input') || element;
  const messages = root.querySelector?.('.v-messages__wrapper') || root.querySelector?.('.v-messages');
  const label = root.querySelector?.('.v-label');
  return { root, messages, label };
}

function ensureMessageContainer(root) {
  let wrapper = root.querySelector('.v-messages__wrapper');
  if (wrapper) return wrapper;

  let messagesContainer = root.querySelector('.v-messages');
  if (!messagesContainer) {
    messagesContainer = document.createElement('div');
    messagesContainer.className = 'v-messages theme--light';
    root.appendChild(messagesContainer);
  }

  wrapper = document.createElement('div');
  wrapper.className = 'v-messages__wrapper';
  messagesContainer.appendChild(wrapper);
  return wrapper;
}

function clearInlineError(element) {
  const { root, label } = findVuetifyWrappers(element);
  if (!root) return;

  const errorMessages = root.querySelectorAll('.ascii-only-msg');
  errorMessages.forEach(msg => msg.parentNode?.removeChild(msg));

  root.classList.remove('error--text', 'v-input--has-state');
  label?.classList.remove('error--text');
}

function showInlineError(element, text, ttl = 2000) {
  const { root, label } = findVuetifyWrappers(element);
  if (!root) return;

  root.classList.add('v-input--has-state', 'error--text');
  label?.classList.add('error--text');

  const wrapper = ensureMessageContainer(root);
  let errorMessage = root.querySelector('.ascii-only-msg');

  if (!errorMessage) {
    errorMessage = document.createElement('div');
    errorMessage.className = 'v-messages__message ascii-only-msg error--text';
    wrapper.appendChild(errorMessage);
  }

  errorMessage.textContent = text || 'ASCII characters only';

  const state = element._asciiOnly = element._asciiOnly || {};
  clearTimeout(state.hideTimer);

  state.hideTimer = setTimeout(() => {
    errorMessage?.parentNode?.removeChild(errorMessage);
    const hasOtherMessages = wrapper.querySelector('.v-messages__message');

    if (!hasOtherMessages) {
      root.classList.remove('error--text', 'v-input--has-state');
      label?.classList.remove('error--text');
    }
  }, ttl);
}

function attach(element, binding) {
  const inputField = element.querySelector('input,textarea');
  if (!inputField) return;

  const options = (typeof binding.value === 'object' && binding.value) || {};
  const mode = options.mode || 'block';
  const message = options.message || 'ASCII characters only';

  clearInlineError(element);

  let lastNotifyTime = 0;

  const notify = () => {
    const now = Date.now();
    if (now - lastNotifyTime < 500) return;
    lastNotifyTime = now;
    showInlineError(element, message, 2000);
  };

  const handleBeforeInput = (event) => {
    if (event.isComposing) return;

    const inputType = event.inputType || '';
    const isTextInsertion = [
      'insertText',
      'insertReplacementText',
      'insertFromPaste',
      'insertFromDrop',
      'insertFromYank',
      'insertFromPasteAsQuotation'
    ].includes(inputType);

    if (isTextInsertion && typeof event.data === 'string' && hasNonAscii(event.data)) {
      notify();
      if (mode === 'block') event.preventDefault();
    }
  };

  const handlePaste = (event) => {
    const clipboardText = (event.clipboardData || window.clipboardData)?.getData('text') || '';
    const cleanedText = sanitizeAscii(clipboardText);

    if (clipboardText !== cleanedText) {
      notify();
      event.preventDefault();

      const selectionStart = inputField.selectionStart || 0;
      const selectionEnd = inputField.selectionEnd || 0;
      const currentValue = inputField.value || '';
      const newValue = currentValue.slice(0, selectionStart) + cleanedText + currentValue.slice(selectionEnd);

      inputField.value = newValue;
      inputField.dispatchEvent(new Event('input', { bubbles: true }));

      const newCursorPosition = selectionStart + cleanedText.length;
      setCursorPosition(inputField, newCursorPosition);
    }
  };

  const handleCompositionEnd = () => {
    const currentValue = inputField.value || '';

    if (hasNonAscii(currentValue)) {
      notify();
      const cleanedValue = sanitizeAscii(currentValue);

      if (cleanedValue !== currentValue) {
        inputField.value = cleanedValue;
        inputField.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  };

  const handleInput = (event) => {
    const originalValue = event.target.value || '';

    if (!hasNonAscii(originalValue)) return;

    notify();
    const cleanedValue = sanitizeAscii(originalValue);

    if (cleanedValue !== originalValue) {
      const originalCursorPosition = inputField.selectionStart || cleanedValue.length;
      inputField.value = cleanedValue;
      inputField.dispatchEvent(new Event('input', { bubbles: true }));

      const charactersRemoved = originalValue.length - cleanedValue.length;
      const newCursorPosition = Math.max(0, originalCursorPosition - Math.max(0, charactersRemoved));
      setCursorPosition(inputField, newCursorPosition);
    }
  };

  const handleFocus = () => clearInlineError(element);

  element._asciiOnly = element._asciiOnly || {};
  Object.assign(element._asciiOnly, {
    onBeforeInput: handleBeforeInput,
    onPaste: handlePaste,
    onCompositionEnd: handleCompositionEnd,
    onInput: handleInput,
    onFocus: handleFocus,
    input: inputField,
  });

  inputField.addEventListener('beforeinput', handleBeforeInput);
  inputField.addEventListener('paste', handlePaste);
  inputField.addEventListener('compositionend', handleCompositionEnd);
  inputField.addEventListener('input', handleInput);
  inputField.addEventListener('focus', handleFocus);
}

function detach(element) {
  const state = element._asciiOnly;
  if (!state?.input) return;

  clearInlineError(element);

  state.input.removeEventListener('beforeinput', state.onBeforeInput);
  state.input.removeEventListener('paste', state.onPaste);
  state.input.removeEventListener('compositionend', state.onCompositionEnd);
  state.input.removeEventListener('input', state.onInput);
  state.input.removeEventListener('focus', state.onFocus);

  clearTimeout(state.hideTimer);
  delete element._asciiOnly;
}

export default {
  install(Vue) {
    Vue.directive('ascii-only', {
      inserted(el, binding) { attach(el, binding); },
      componentUpdated(el, binding) {
        detach(el);
        attach(el, binding);
      },
      unbind(el) { detach(el); },
    });
  }
};
